import {
  type Edge,
  getIncomers,
  getOutgoers,
  type NodePositionChange,
} from "reactflow";
import {
  type JourneyNode,
  isExecutableJourneyNode,
  isJourneyNoteNode,
  type ExecutableJourneyNode,
  type JourneyNoteDetails,
} from "../types";
import { JOURNEY_BRANCH_NODES } from "../constants";

const STATIC_ARRAY = [];

/**
 * Get all outgoers of a node. Safe version of getOutgoers that only returns executable nodes.
 *
 * @param node - The node to get the outgoers of.
 * @param nodes - The list of all nodes in the graph.
 * @param edges - The list of all edges in the graph.
 * @returns A list of executable nodes that are outgoers of the given node.
 */
export function getOutgoersSafe(
  node: JourneyNode,
  nodes: JourneyNode[],
  edges: Edge[],
): ExecutableJourneyNode[] {
  if (!isExecutableJourneyNode(node) || isJourneyNoteNode(node)) {
    return STATIC_ARRAY;
  }
  const executableNodes = nodes.filter(isExecutableJourneyNode);
  return getOutgoers(node, executableNodes, edges);
}

/**
 * Get all incomers of a node. Safe version of getIncomers that only returns executable nodes.
 *
 * @param node - The node to get the incomers of.
 * @param nodes - The list of all nodes in the graph.
 * @param edges - The list of all edges in the graph.
 * @returns A list of executable nodes that are incomers of the given node.
 */
export function getIncomersSafe(
  node: JourneyNode,
  nodes: JourneyNode[],
  edges: Edge[],
): ExecutableJourneyNode[] {
  if (!isExecutableJourneyNode(node) || isJourneyNoteNode(node)) {
    return STATIC_ARRAY;
  }
  const executableNodes = nodes.filter(isExecutableJourneyNode);
  return getIncomers(node, executableNodes, edges);
}

/**
 * Compute updated node and note positions for a set of React Flow position changes.
 * - Applies parent/branch movement rules for executable journey nodes
 * - Applies the same offset to all ids included in the positionChanges (to support multi-select for both nodes and notes)
 * - Ignores graph relationship logic for notes; they simply move if included in changes or selection-derived ids
 */
export function updateNodePosition(
  changes: NodePositionChange[],
  nodes: ExecutableJourneyNode[],
  notes: JourneyNoteDetails[],
  edges: Edge[],
): {
  updatedNodes: ExecutableJourneyNode[];
  updatedNotes: JourneyNoteDetails[];
} {
  const firstChange = changes?.[0];
  if (!firstChange || !firstChange.position) {
    return { updatedNodes: nodes, updatedNotes: notes };
  }

  // Identify dragged entity (prefer nodes; fallback to notes)
  const draggedNode = nodes.find((n) => n.id === firstChange.id);
  const draggedNote = draggedNode
    ? undefined
    : notes.find((n) => n.id === firstChange.id);

  if (!draggedNode && !draggedNote) {
    return { updatedNodes: nodes, updatedNotes: notes };
  }

  const originalPosition = draggedNode?.position ??
    // Notes on canvas always have a position; guard just in case
    draggedNote?.position ?? { x: 0, y: 0 };

  const xOffset = firstChange.position.x - originalPosition.x;
  const yOffset = firstChange.position.y - originalPosition.y;

  const idsToUpdate: Set<string> = new Set();

  // Checked on the node rather than its type because a Function node has branches only once it has a
  // success condition.
  const draggedBranchNodes =
    draggedNode && isExecutableJourneyNode(draggedNode)
      ? getOutgoersSafe(draggedNode, nodes, edges).filter((node) =>
          JOURNEY_BRANCH_NODES.includes(node.data.config.type),
        )
      : [];

  // Apply executable node relationship rules when the dragged entity is a node
  if (draggedNode && draggedBranchNodes.length > 0) {
    // If dragging a parent node, move all its branch nodes
    idsToUpdate.add(draggedNode.id);
    draggedBranchNodes.forEach(({ id }) => idsToUpdate.add(id));
  } else if (
    draggedNode &&
    isExecutableJourneyNode(draggedNode) &&
    JOURNEY_BRANCH_NODES.includes(draggedNode.data.config.type)
  ) {
    // If dragging a branch node, move the parent and all sibling branches
    const parentNodes = getIncomersSafe(draggedNode, nodes, edges);
    const parentNode = parentNodes[0];
    if (parentNode) {
      const branchNodes = getOutgoersSafe(parentNode, nodes, edges);
      idsToUpdate.add(parentNode.id);
      branchNodes.forEach(({ id }) => idsToUpdate.add(id));
    }
  } else if (draggedNode) {
    // Regular node
    idsToUpdate.add(draggedNode.id);
  } else if (draggedNote) {
    // Dragging a note (no graph relationships)
    idsToUpdate.add(draggedNote.id);
  }

  // Always include all ids in the position changes to support multi-select drags
  for (const change of changes) {
    if (change?.id) {
      idsToUpdate.add(change.id);
    }
  }

  const updatedNodes = nodes.map((node) => {
    if (!idsToUpdate.has(node.id)) return node;
    const current = node.position ?? { x: 0, y: 0 };
    return {
      ...node,
      position: {
        x: current.x + xOffset,
        y: current.y + yOffset,
      },
    };
  });

  const updatedNotes = notes.map((note) => {
    if (!idsToUpdate.has(note.id)) return note;
    const current = note.position ?? { x: 0, y: 0 };
    return {
      ...note,
      position: {
        x: current.x + xOffset,
        y: current.y + yOffset,
      },
    } as JourneyNoteDetails;
  });

  return { updatedNodes, updatedNotes };
}

/**
 * Return a new nodes array where all direct outgoer branch nodes of the given parent
 * are offset vertically according to the change in the count of visible attached notes.
 *
 * Pure function: does not mutate inputs; no side effects.
 */
export function offsetBranchNodesForAttachedNotesDelta(
  nodes: ExecutableJourneyNode[],
  edges: Edge[],
  parentNodeId: string,
  deltaVisible: number,
  noteAttachedMinHeight: number,
): ExecutableJourneyNode[] {
  if (deltaVisible === 0) return nodes;
  const parentNode = nodes.find((n) => n.id === parentNodeId);
  if (!parentNode) return nodes;
  const branchNodes = getOutgoersSafe(parentNode, nodes, edges);
  if (branchNodes.length === 0) return nodes;

  const yOffset = deltaVisible * noteAttachedMinHeight;

  const branchIds = new Set(branchNodes.map((bn) => bn.id));
  return nodes.map((n) => {
    if (!branchIds.has(n.id)) return n;
    const cur = n.position ?? { x: 0, y: 0 };
    return {
      ...n,
      position: { x: cur.x, y: cur.y + yOffset },
    };
  });
}
